import * as React from 'react'

import * as searchUtil from './search'
import { SortType } from './sort'

export type Query = {
  value: searchUtil.SearchQuery
  add: (index: string, bucket: string) => searchUtil.SearchQuery
  remove: (index: string, bucket: string) => searchUtil.SearchQuery
  reset: () => searchUtil.SearchQuery
  sort: (sortType: SortType) => searchUtil.SearchQuery
  set: (query: searchUtil.SearchQuery) => void
  text: (query: string) => searchUtil.SearchQuery
}

export function useQuery(initialQuery: searchUtil.SearchQuery): Query {
  const [query, setQuery] = React.useState<searchUtil.SearchQuery>(initialQuery)

  return { value: query, add, remove, reset, sort, text, set: setQuery }

  function add(index: string, bucket: string): searchUtil.SearchQuery {
    if (!(index in query.filters)) {
      const nextQuery = {
        ...query,
        filters: { ...query.filters, [index]: [bucket] },
      }
      setQuery(nextQuery)
      return nextQuery
    } else {
      const filter = query.filters[index]
      if (!filter.includes(bucket)) {
        const nextQuery = {
          ...query,
          filters: {
            ...query.filters,
            [index]: [...filter, bucket],
          },
        }
        setQuery(nextQuery)
        return nextQuery
      }
    }
    return query
  }

  function remove(index: string, bucket: string): searchUtil.SearchQuery {
    if (index in query.filters) {
      const filter = query.filters[index]
      if (filter.length === 1) {
        const copy = { ...query.filters }
        delete copy[index]
        const nextQuery = {
          ...query,
          filters: copy,
        }
        setQuery(nextQuery)
        return nextQuery
      } else {
        const bucketIndex = filter.indexOf(bucket)
        const nextQuery = {
          ...query,
          filters: {
            ...query.filters,
            [index]: [...filter.slice(0, bucketIndex), ...filter.slice(bucketIndex + 1)],
          },
        }
        setQuery(nextQuery)
        return nextQuery
      }
    }
    return query
  }

  function reset(): searchUtil.SearchQuery {
    setQuery(initialQuery)
    return initialQuery
  }

  function sort(sortType: SortType): searchUtil.SearchQuery {
    const nextQuery = {
      ...query,
      sortType: sortType,
    }
    setQuery(nextQuery)
    return nextQuery
  }

  function text(text: string) {
    const nextQuery = { ...query, text }
    setQuery(nextQuery)
    return nextQuery
  }
}
